import { Camera, LineSegments, Mesh, PerspectiveCamera, Scene, Vector3 } from 'three';

/**
 * Render quality level (0-10)
 * 0 = wireframe
 * 1 = lowest quality
 * 10 = max quality
 */
export type RenderQuality = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

/**
 * Camera types that can be visualized
 */
export type VisualizableCamera = Camera | PerspectiveCamera;

/**
 * Objects that represent a camera's visualization
 */
export interface CameraHelperObjects {
	/** Cone showing camera direction */
	cone: Mesh;
	/** Line from camera to target */
	targetLine: LineSegments;
}

/**
 * Configuration for CameraHelper
 */
export interface CameraHelperConfig {
	/** Scene to add helper objects to */
	scene: Scene;
	/** Cone radius (default: 3.0) */
	coneRadius?: number;
	/** Cone height (default: 15.0) */
	coneHeight?: number;
	/** Cone color (default: 0x00ffff - cyan) */
	coneColor?: number;
	/** Line color (default: 0xff0000 - red) */
	lineColor?: number;
	/** Whether helper is initially visible (default: false) */
	visible?: boolean;
	/** Initial target position for the camera */
	targetPosition?: Vector3;
	/** Render quality 0-10 (default: 5) */
	quality?: RenderQuality;
}

/**
 * Configuration for CameraHelperManager
 */
export interface CameraHelperManagerConfig {
	/** Scene reference */
	scene: Scene;
	/** Global cone radius (default: 3.0) */
	coneRadius?: number;
	/** Global cone height (default: 15.0) */
	coneHeight?: number;
	/** Cone color (default: 0x00ffff - cyan) */
	coneColor?: number;
	/** Line color (default: 0xff0000 - red) */
	lineColor?: number;
	/** Initial visibility state (default: false) */
	visible?: boolean;
	/** Render quality 0-10 (default: 5) */
	quality?: RenderQuality;
}

/**
 * Default values for camera helper configuration
 */
export const CAMERA_HELPER_DEFAULTS = {
	CONE_RADIUS: 3.0,
	CONE_HEIGHT: 15.0,
	CONE_COLOR: 0x00ffff, // Cyan
	LINE_COLOR: 0xff0000, // Red
	QUALITY: 5 as RenderQuality
} as const;

/**
 * Get geometry segment counts based on quality level
 * @param quality - Quality level 0-10
 * @returns Object with radialSegments and heightSegments for cone
 */
export const getQualitySegments = (quality: RenderQuality): { radial: number; height: number } => {
	if (quality === 0) {
		// Wireframe mode - minimal segments
		return { radial: 4, height: 1 };
	}
	// Scale from 4 (quality 1) to 32 (quality 10)
	const radial = Math.round(4 + ((quality - 1) * 28) / 9);
	const height = Math.max(1, Math.round(quality / 2));
	return { radial, height };
};

